// src/hooks/usePermissions.js
import { useMemo } from 'react';

/**
 * Custom hook for managing organization-based permissions
 * 
 * @param {Object} userProfile - User profile object with roles array
 * @returns {Object} Permission flags and helper functions
 */
const usePermissions = (userProfile) => {
  const permissions = useMemo(() => {
    const roles = userProfile?.roles || [];
    
    // Helper to check if a role exists (supports both string array and object array formats)
    const hasRole = (roleName) => {
      const normalizedRoleName = roleName.toLowerCase();
      return roles.some(role => {
        const roleValue = typeof role === 'string' ? role : role.name;
        return roleValue.toLowerCase() === normalizedRoleName;
      });
    };
    
    const isFullAdmin = hasRole('Full Admin');
    const isOrgAdmin = hasRole('Organization Admin');
    const isOrgUser = hasRole('Organization User') || hasRole('Organization Member');
    
    return {
      // Role checks
      isFullAdmin,
      isOrgAdmin,
      isOrgUser,
      
      // Permission flags
      canCreateOrganization: isFullAdmin || isOrgAdmin,
      canUpdateOrganization: isFullAdmin || isOrgAdmin,
      canEditOrganization: isFullAdmin || isOrgAdmin, // Alias for canUpdateOrganization
      canDeleteOrganization: isFullAdmin || isOrgAdmin,
      canAuthorizeOrganization: isFullAdmin || isOrgAdmin,
      canUnauthorizeOrganization: isFullAdmin,
      canManageUsers: isFullAdmin || isOrgAdmin,
      canViewOrganizationUsers: isFullAdmin || isOrgAdmin || isOrgUser, // All roles can view
      canManageOrganizationUsers: isFullAdmin || isOrgAdmin, // Alias for canManageUsers
      canViewDevStreams: isFullAdmin, // Can be overridden by org settings
      canManageInstallations: isFullAdmin || isOrgAdmin || isOrgUser,
      
      // Helper functions
      hasRole,
      hasAnyRole: (...roleNames) => roleNames.some(roleName => hasRole(roleName)),
      hasAllRoles: (...roleNames) => roleNames.every(roleName => hasRole(roleName)),
      
      // Permission error messages
      getPermissionError: (action) => {
        const errors = {
          create: 'You do not have permission to create organizations.',
          edit: 'You do not have permission to edit organizations.',
          update: 'You do not have permission to update organizations.',
          delete: 'You do not have permission to delete organizations.',
          authorize: 'You do not have permission to authorize organizations.',
          unauthorize: 'Only Full Admins can unauthorize organizations.',
          manageUsers: 'You do not have permission to manage organization users.',
        };
        return errors[action] || 'You do not have permission to perform this action.';
      },
    };
  }, [userProfile]);
  
  return permissions;
};

export default usePermissions;
